import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { AlertTriangle, Info, Shield, Scale, ExternalLink, Users, Eye, HelpCircle, FileText, Lock } from 'lucide-react';

const DisclaimerSection = ({ icon: Icon, title, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
        <Icon size={20} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-4 ml-1">
      {children}
    </div>
  </div>
);

const Disclaimer = () => {
  return (
    <StaticContentPage title="Disclaimer" lastUpdated="May 14, 2026">
      <div className="max-w-3xl mb-12">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to Chooziq.
        </p>
        <p className="text-gray-600 leading-relaxed">
          This Disclaimer governs the use of the Chooziq platform, website, communities, live rooms, and related services (collectively, the “Services”). By accessing or using Chooziq, you acknowledge and agree to this Disclaimer.
        </p>
      </div>

      <DisclaimerSection icon={Info} title="1. General Information">
        <p>Chooziq is a realtime interaction and collaboration platform that enables users, communities, organizations, educators, presenters, and participants to engage through live discussions, brainstorming sessions, polls, collaborative activities, and community interactions.</p>
        <p>The information, discussions, opinions, ideas, resources, and content shared through the Services are provided for general informational and collaborative purposes only.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={FileText} title="2. User-Generated Content">
        <p>Chooziq hosts content created and shared by users, including discussions, comments, questions, brainstorming ideas, polls, files, resources, and community posts.</p>
        <p>We do not guarantee the accuracy, completeness, reliability, legality, or usefulness of user-generated content. The views and opinions expressed by users, speakers, organizers, or communities do not necessarily reflect the views of Chooziq.</p>
        <p>Users are solely responsible for the content they create, post, share, or distribute through the Services.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={Scale} title="3. No Professional Advice">
        <p>Content available on Chooziq does not constitute professional advice of any kind, including but not limited to:</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {['Legal advice', 'Financial advice', 'Medical advice', 'Educational certification', 'Business advice', 'Professional consulting'].map(item => (
            <li key={item} className="flex items-center gap-2 text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-indigo"></span>
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 italic">Users should seek qualified professional guidance before making decisions based on information obtained through the Services.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={AlertTriangle} title="4. Realtime Interactions and Availability">
        <p>Chooziq provides realtime engagement and collaboration features; however, we do not guarantee uninterrupted availability, continuous access, error-free performance, or permanent availability of sessions or content.</p>
        <p>Realtime interactions may be affected by internet connectivity, device limitations, third-party service interruptions, maintenance, or technical issues beyond our control.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={ExternalLink} title="5. External Links and Third-Party Services">
        <p>The Services may contain links to external websites, platforms, or third-party services. Chooziq does not control, endorse, or assume responsibility for third-party content, products, services, policies, or practices.</p>
        <p>Accessing third-party services is done at your own risk.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={Users} title="6. Community and Event Participation">
        <p>Participation in communities, events, discussions, live rooms, and collaborative sessions is voluntary. Chooziq is not responsible for interactions between users, user conduct, community behavior, organizer decisions, or speaker statements.</p>
        <p>Users are responsible for exercising appropriate judgment and discretion when participating in public or private communities and sessions.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={Shield} title="7. Content Moderation">
        <p>While Chooziq may implement moderation tools and community guidelines, we do not guarantee that all content will be monitored, reviewed, or removed promptly.</p>
        <p>We reserve the right, but not the obligation, to remove content, restrict access, or moderate platform activity at our discretion.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={Lock} title="8. Limitation of Liability">
        <p className="font-bold text-gray-900">To the fullest extent permitted by applicable law, Chooziq and its affiliates shall not be liable for any direct, indirect, incidental, consequential, special, or punitive damages arising from:</p>
        <ul className="list-disc ml-6 space-y-2 mt-4 text-sm">
          <li>Use of the Services or participation in communities or events</li>
          <li>User-generated content or reliance on information provided</li>
          <li>Realtime interactions or platform interruptions</li>
          <li>Loss of data</li>
        </ul>
        <p className="mt-4 font-medium italic">Use of the Services is at your own risk.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={HelpCircle} title="9. No Guarantees or Warranties">
        <p>The Services are provided on an “as is” and “as available” basis without warranties of any kind. Chooziq does not warrant that the platform will meet user expectations, be uninterrupted or secure, or that content will be accurate.</p>
      </DisclaimerSection>

      <DisclaimerSection icon={Eye} title="10. Intellectual Property Responsibility">
        <p>Users are responsible for ensuring that content they upload, share, or distribute through Chooziq does not violate intellectual property rights, copyrights, or trademarks.</p>
      </DisclaimerSection>

      <div className="mt-16 p-8 bg-gray-900 rounded-[40px] text-white text-center">
        <h3 className="text-xl font-bold mb-4">Questions?</h3>
        <p className="text-gray-400 mb-6">If you have questions regarding this Disclaimer, please contact us.</p>
        <a href="mailto:legal@chooziq.com" className="text-lg font-bold text-brand-indigo hover:underline">legal@chooziq.com</a>
      </div>
    </StaticContentPage>
  );
};

export default Disclaimer;
